// pages/ContributorVideoPage.jsx — Anamoria SPA
// v2.1 — NEW-163 Increment B (2026-10-30): a member can respond to the leader's note.
//   - Reads `?note=<group_notes id>` from location.search (it is in the URL, so it survives refresh and the
//     voice/video round trip) and quotes the note at the top of the capture + review screens
//     (RespondingToNote; copy v1.0 B-8). The note is resolved from space.groupNotes already loaded here.
//   - handleSave: the create payload carries `groupNoteId` only when the id resolves against
//     space.groupNotes (unknown → untagged; the server validates regardless, 400 INVALID_GROUP_NOTE).
//   - Share to the group with a loved-one name from ContributorSaveActions v1.4 (opts.lovedOneName):
//     PATCH {prefix}/{spaceId} { lovedOneName } (the live body-action) BEFORE the create so the
//     attribution join finds it on first render. If that PATCH fails nothing is created (the page's
//     standard error; the member can retry). Non-group / flag-off: payload byte-identical to v2.0.
//   SPA build spec v0.2 §6 (C-e confirmed at build: single create body, space GET on mount).
// E3b (Consent PR E, Oct 4, 2026): edit mode passes space/api/keepCopyExcluded to ContributorEditSharing;
//   handleKeepCopy PATCHes keepCopyExcluded (+ keepCopyShownHash on re-include); share state carries keepCopyExcluded.
// E3a (Consent PR E, Oct 4, 2026): handleSave(visibilityPref, keepCopyOpts) forwards keepCopy + keepCopyShownHash
//   from ContributorSaveActions on a first share; NOTICE_CHANGED/NOTICE_UNAVAILABLE -> plain error. Dark while the flag is off.
// v2.0 — NEW-128: contributor video fast-follow (2026-09-28)
//
// Adds the two pieces deliberately deferred out of v1.0 (NEW-121):
//   - Edit/re-record: reads location.state.editMemory (same pattern as
//     ContributorRecordPage.jsx v1.10 — isEditMode, PATCH via generic
//     s3Key/duration/mimeType, ContributorEditSharing + ContributorEditActions
//     for the edit-mode footer/sharing controls). Existing video is fetched via
//     /media/playback/:playbackKey, mirroring ContributorMemoryDetailPage.jsx's
//     video-fetch pattern.
//   - Library-picker: validateVideoFile/handleLibraryClick/handleFileSelected
//     and the MAX_FILE_SIZE (100MB) / MAX_DURATION_SEC (300s) checks, ported
//     verbatim from the owner's VideoPage.jsx — available in both create and
//     re-record contexts, matching the owner page. These same constants are
//     the source of the "re-record enforces the same caps as record" parity:
//     the live-recording duration cap is already inherited for free via the
//     unmodified useVideoRecorder() hook (MAX_RECORDING_MS), and the backend
//     size cap (MAX_VIDEO_BYTES) already applies to any video upload-url
//     request regardless of create/edit context — only these two client-side
//     library-file checks needed to be ported explicitly.
//
// v1.0 — NEW-121: contributor video capture (2026-09-27)
// (see git history for the full v1.0 header — CREATE-ONLY / RECORD-ONLY scope,
// now superseded by the above.)
//
// Route: /contributing/:spaceId/video (linked mode). Flag-gated two ways
// (contributor_video_enabled, deny-by-default) — unchanged from v1.0.
//
// Create upload flow: POST {prefix}/{spaceId}/upload-url with
//   { mimeType, mediaType: 'video', fileSize } → putS3(uploadUrl, blob, mimeType)
//   → POST {prefix}/{spaceId}/memories with { type: 'video', s3Key, mimeType,
//   title, ...visibilityPref } — NO duration field on create (populated later,
//   asynchronously, by anamoria-video-postprocess; unchanged from v1.0, for
//   both camera-recorded and library-selected video).
//
// Edit/re-record flow: same upload-url + putS3 as above, then PATCH
//   {prefix}/{spaceId}/memories/{id} with { s3Key, mimeType, duration, title }
//   (generic field names — matches the contributor endpoint's existing voice
//   re-record shape, NOT the owner endpoint's newVideoS3Key-prefixed shape).
//   `duration` IS sent here (unlike create), mirroring voice's own edit PATCH.
//
// CSS: Reuses VideoPage.module.css (zero CSS duplication), per plan §6.1.

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { useVideoRecorder } from '../hooks/useVideoRecorder';
import { getContributorAccess } from '../api/contributorAccess';
import LovedOneBar from '../components/LovedOneBar';
import ContributorBottomNav from '../components/ContributorBottomNav';
import ContributorSaveActions from '../components/ContributorSaveActions';
import RespondingToNote from '../components/RespondingToNote';   // v2.1 (NEW-163)
import { readNoteParam, findGroupNote } from '../utils/contributorCapture';   // v2.1 (NEW-163)
import ContributorEditSharing from '../components/ContributorEditSharing';
import ContributorEditActions from '../components/ContributorEditActions';
import styles from './VideoPage.module.css';

const TOAST_DURATION_MS = 2500;

// NEW-128: library-picker constants, ported verbatim from owner VideoPage.jsx
// (Decision #8 / Decision #6 amended) so the caps stay identical wherever
// they're enforced client-side.
const ACCEPTED_VIDEO_TYPES = ['video/mp4', 'video/webm', 'video/quicktime'];
const ACCEPTED_VIDEO_EXTENSIONS = '.mp4,.webm,.mov';
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
const MAX_DURATION_SEC = 300; // 5 minutes

function formatDurationSec(val) {
  const s = Math.round(Number(val) || 0);
  return `${Math.floor(s / 60)}:${(s % 60).toString().padStart(2, '0')}`;
}

/** Validate a user-selected video file. Returns { valid, error, duration }.
 * Ported verbatim from owner VideoPage.jsx. */
function validateVideoFile(file) {
  return new Promise((resolve) => {
    if (!ACCEPTED_VIDEO_TYPES.includes(file.type)) {
      resolve({ valid: false, error: 'Unsupported file type. Please choose an MP4, WebM, or MOV video.' });
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      resolve({ valid: false, error: 'File is too large. Maximum size is 100 MB.' });
      return;
    }
    const video = document.createElement('video');
    video.preload = 'metadata';
    const url = URL.createObjectURL(file);
    video.src = url;

    video.onloadedmetadata = () => {
      URL.revokeObjectURL(url);
      if (video.duration > MAX_DURATION_SEC) {
        resolve({
          valid: false,
          error: `Video is too long (${formatDurationSec(video.duration)}). Maximum is ${formatDurationSec(MAX_DURATION_SEC)}.`,
        });
      } else {
        resolve({ valid: true, error: null, duration: Math.round(video.duration) });
      }
    };

    video.onerror = () => {
      URL.revokeObjectURL(url);
      resolve({ valid: false, error: 'Could not read this video file. Please try a different file.' });
    };
  });
}

/* ═══════════════════════════════════════
   CONTRIBUTOR VIDEO PAGE
   ═══════════════════════════════════════ */

export default function ContributorVideoPage({ mode = 'linked' }) {
  const { spaceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // v2.1 (NEW-163): the group note being responded to, from ?note= (null when absent).
  const groupNoteId = readNoteParam(new URLSearchParams(location.search || ''));
  const { getAccessTokenSilently } = useAuth0();

  const navBase = '/contributing';
  const expiredMsg = 'Your session has expired. Please sign in again.';
  const access = useMemo(
    () => getContributorAccess(mode, getAccessTokenSilently),
    [mode, getAccessTokenSilently]
  );

  // NEW-128: edit mode — pre-populate from an existing video memory.
  const editMemory = location.state?.editMemory || null;
  const isEditMode = !!editMemory;

  const {
    recordingState,
    videoBlob,
    mimeType,
    duration,
    durationFormatted,
    atWarning,
    error: recorderError,
    previewStream,
    facingMode,
    permissionState,
    startRecording,
    pauseRecording,
    resumeRecording,
    stopRecording,
    reRecord,
    toggleCamera,
    checkPermission,
  } = useVideoRecorder();

  /* ─── Space data ─── */
  const [space, setSpace] = useState(null);
  const [loadingData, setLoadingData] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoadingData(true);
      try {
        const data = await access.api.get(`${access.prefix}/${spaceId}`);
        if (!cancelled) setSpace(data);
      } catch (err) {
        if (cancelled) return;
        if (err.error === 'INVALID_SESSION' || err.error === 'NO_SESSION_TOKEN' || err.status === 401) {
          setLoadError(expiredMsg);
        } else {
          setLoadError('Could not load this space.');
        }
      } finally {
        if (!cancelled) setLoadingData(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [spaceId, access, expiredMsg]);

  // Client-side guard: this route should only be reachable via the flagged-on
  // Video button, but a typed URL could reach it directly while the flag is
  // off for this space. Redirect rather than show a recording screen that
  // would fail server-side on save (server is the real gate — this is UX only).
  useEffect(() => {
    if (!loadingData && space && !space.contributorVideoEnabled) {
      navigate(`${navBase}/${spaceId}/memories`, { replace: true });
    }
  }, [loadingData, space, navigate, spaceId]);

  // Check camera permission on mount (matches VideoPage.jsx BPR §1.11)
  useEffect(() => {
    checkPermission();
  }, [checkPermission]);

  /* ─── NEW-128: fetch existing video for edit mode ───
     Mirrors ContributorMemoryDetailPage.jsx's video-fetch pattern — the feed
     only returns videoNote.playbackKey (not a signed URL), so it's resolved
     here via /media/playback/:key. */
  const [existingVideoUrl, setExistingVideoUrl] = useState(null);
  const [loadingExistingVideo, setLoadingExistingVideo] = useState(isEditMode);

  useEffect(() => {
    if (!isEditMode || !editMemory?.videoNote?.playbackKey) {
      setLoadingExistingVideo(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const data = await access.api.get(
          `/media/playback/${encodeURIComponent(editMemory.videoNote.playbackKey)}`
        );
        if (!cancelled) setExistingVideoUrl(data.playbackUrl);
      } catch (err) {
        console.error('Failed to load existing video:', err);
      } finally {
        if (!cancelled) setLoadingExistingVideo(false);
      }
    })();
    return () => { cancelled = true; };
  }, [isEditMode, editMemory, access]);

  /* ─── Review + save state ─── */
  const [memoryLabel, setMemoryLabel] = useState(editMemory?.title || '');
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [showSavedPill, setShowSavedPill] = useState(false);
  const [wantsReRecord, setWantsReRecord] = useState(false);

  // NEW-128: library upload state — ported verbatim from owner VideoPage.jsx.
  const fileInputRef = useRef(null);
  const [libraryFile, setLibraryFile] = useState(null);
  const [libraryBlob, setLibraryBlob] = useState(null);
  const [libraryDuration, setLibraryDuration] = useState(0);
  const [libraryMimeType, setLibraryMimeType] = useState('');
  const [libraryError, setLibraryError] = useState('');

  const reviewVideoRef = useRef(null);
  const liveVideoRef = useRef(null);
  const toastTimerRef = useRef(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  // Attach live stream to <video> element for camera preview
  useEffect(() => {
    if (liveVideoRef.current && previewStream) {
      liveVideoRef.current.srcObject = previewStream;
    }
  }, [previewStream]);

  // ─── Merged "active" values: camera recording OR library file ───
  const activeBlob = videoBlob || libraryBlob;
  const activeDuration = videoBlob ? duration : (libraryBlob ? libraryDuration : (editMemory?.videoNote?.duration || 0));
  const activeDurationFormatted = videoBlob
    ? durationFormatted
    : formatDurationSec(libraryBlob ? libraryDuration : (editMemory?.videoNote?.duration || 0));
  const activeMimeType = videoBlob ? mimeType : libraryMimeType;
  const hasNewFootage = !!activeBlob;

  // Create blob URL for review screen playback (camera recording OR library file)
  useEffect(() => {
    if (activeBlob) {
      const url = URL.createObjectURL(activeBlob);
      setPreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setPreviewUrl(null);
  }, [activeBlob]);

  // The video actually shown on the review screen: a fresh blob takes
  // priority; otherwise, in edit mode, the existing remote video.
  const displayVideoUrl = previewUrl || (isEditMode ? existingVideoUrl : null);

  // Force first-frame display (same trick as VideoPage.jsx — without it the
  // review player shows black until play is tapped).
  useEffect(() => {
    const video = reviewVideoRef.current;
    if (!video || !displayVideoUrl) return;
    function seekToFirstFrame() {
      if (video.duration && video.currentTime === 0) video.currentTime = 0.1;
    }
    video.addEventListener('loadeddata', seekToFirstFrame);
    return () => video.removeEventListener('loadeddata', seekToFirstFrame);
  }, [displayVideoUrl]);

  /* ─── Custom video player state (review screen) ─── */
  const [videoPlaying, setVideoPlaying] = useState(false);
  const [videoProgress, setVideoProgress] = useState(0);
  const [videoCurrentTime, setVideoCurrentTime] = useState('0:00');

  useEffect(() => {
    const video = reviewVideoRef.current;
    if (!video) return;
    function handleTimeUpdate() {
      if (video.duration) {
        setVideoProgress((video.currentTime / video.duration) * 100);
        setVideoCurrentTime(formatDurationSec(video.currentTime));
      }
    }
    function handleEnded() { setVideoPlaying(false); setVideoProgress(0); setVideoCurrentTime('0:00'); }
    function handlePause() { setVideoPlaying(false); }
    function handlePlay() { setVideoPlaying(true); }
    video.addEventListener('timeupdate', handleTimeUpdate);
    video.addEventListener('ended', handleEnded);
    video.addEventListener('pause', handlePause);
    video.addEventListener('play', handlePlay);
    return () => {
      video.removeEventListener('timeupdate', handleTimeUpdate);
      video.removeEventListener('ended', handleEnded);
      video.removeEventListener('pause', handlePause);
      video.removeEventListener('play', handlePlay);
    };
  });

  function toggleVideoPlayback() {
    const video = reviewVideoRef.current;
    if (!video) return;
    if (videoPlaying) { video.pause(); } else { video.play(); }
  }

  function handleProgressClick(e) {
    const video = reviewVideoRef.current;
    if (!video || !video.duration) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const ratio = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
    video.currentTime = ratio * video.duration;
    setVideoProgress(ratio * 100);
    setVideoCurrentTime(formatDurationSec(video.currentTime));
  }

  useEffect(() => {
    return () => { if (reviewVideoRef.current) reviewVideoRef.current.pause(); };
  }, []);

  /* ─── Cleanup toast timer on unmount ─── */
  useEffect(() => {
    return () => { if (toastTimerRef.current) clearTimeout(toastTimerRef.current); };
  }, []);

  /* ─── NEW-128: library file picker — ported verbatim from owner VideoPage.jsx ─── */
  function handleLibraryClick() {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
      fileInputRef.current.click();
    }
  }

  async function handleFileSelected(e) {
    const file = e.target.files?.[0];
    if (!file) return;

    setLibraryError('');
    const result = await validateVideoFile(file);

    if (!result.valid) {
      setLibraryError(result.error);
      return;
    }

    setLibraryFile(file);
    setLibraryBlob(file);
    setLibraryDuration(result.duration);
    setLibraryMimeType(file.type);
  }

  function resetLibrarySelection() {
    setLibraryFile(null);
    setLibraryBlob(null);
    setLibraryDuration(0);
    setLibraryMimeType('');
    setLibraryError('');
  }

  /* ─── Save handler ─── */
  // E3a: keepCopyOpts = { keepCopy: true, keepCopyShownHash } from the share picker (first share), else undefined.
  const handleSave = useCallback(async (visibilityPref, keepCopyOpts) => {
    // Edit mode, label-only change (no new footage captured/selected).
    if (isEditMode && !hasNewFootage) {
      setUploadError('');
      setUploading(true);
      try {
        const api = access.api;
        await api.patch(`${access.prefix}/${spaceId}/memories/${editMemory.id}`, {
          title: memoryLabel.trim() || null,
        });
        setUploading(false);
        setShowSavedPill(true);
        toastTimerRef.current = setTimeout(() => {
          navigate(`${navBase}/${spaceId}/memories`, { replace: true });
        }, TOAST_DURATION_MS);
      } catch (err) {
        console.error('Save error:', err);
        if (err.error === 'INVALID_SESSION' || err.error === 'NO_SESSION_TOKEN' || err.status === 401) {
          setUploadError(expiredMsg);
        } else {
          setUploadError("Something didn't save. Please try again.");
        }
        setUploading(false);
      }
      return;
    }

    // Create, or edit with new footage (camera recording or library file).
    if (!activeBlob) return;
    setUploadError('');
    setUploading(true);

    const baseMimeType = (activeMimeType || 'video/webm').split(';')[0];

    try {
      // v2.1 (NEW-163): resolve the note being responded to against the space's notes; unknown → untagged.
      const respondNoteId = (!isEditMode && groupNoteId && space?.isGroupSpace === true && space?.groupSpacesEnabled === true && findGroupNote(space.groupNotes, groupNoteId)) ? groupNoteId : null;
      // v2.1 (NEW-163): loved-one name entered on the share step → save it BEFORE the create (body-action
      // PATCH on the contributor space route), so "remembering {name}" is there on first render.
      if (!isEditMode && visibilityPref === 'group' && typeof keepCopyOpts?.lovedOneName === 'string') {
        await access.api.patch(`${access.prefix}/${spaceId}`, { lovedOneName: keepCopyOpts.lovedOneName });
      }
      const api = access.api;

      const uploadData = await api.post(`${access.prefix}/${spaceId}/upload-url`, {
        mimeType: baseMimeType,
        mediaType: 'video',
        fileSize: activeBlob.size,
      });

      await api.putS3(uploadData.uploadUrl, activeBlob, baseMimeType);

      if (isEditMode) {
        // NEW-128: PATCH with the contributor endpoint's existing generic
        // field names (s3Key/duration/mimeType) — matches the voice re-record
        // shape already in place, NOT the owner endpoint's prefixed names.
        await api.patch(`${access.prefix}/${spaceId}/memories/${editMemory.id}`, {
          s3Key: uploadData.s3Key,
          mimeType: baseMimeType,
          duration: Math.round(activeDuration),
          title: memoryLabel.trim() || null,
        });
      } else {
        await api.post(`${access.prefix}/${spaceId}/memories`, {
          type: 'video',
          s3Key: uploadData.s3Key,
          mimeType: baseMimeType,
          // No `duration` here on create — server-side, video's duration is
          // populated later by anamoria-video-postprocess (single-writer
          // invariant, unchanged from v1.0 for both camera and library video).
          ...(memoryLabel.trim() ? { title: memoryLabel.trim() } : {}),
          ...(space?.groupSpacesEnabled && typeof visibilityPref === 'string' ? { visibilityPref } : {}),
          ...(keepCopyOpts?.keepCopy === true && keepCopyOpts.keepCopyShownHash ? { keepCopy: true, keepCopyShownHash: keepCopyOpts.keepCopyShownHash } : {}),   // E3a
          ...(respondNoteId ? { groupNoteId: respondNoteId } : {}),   // v2.1 (NEW-163): only when the id resolves in space.groupNotes
        });
      }

      setUploading(false);
      setShowSavedPill(true);
      toastTimerRef.current = setTimeout(() => {
        navigate(`${navBase}/${spaceId}/memories`, { replace: true });
      }, TOAST_DURATION_MS);
    } catch (err) {
      console.error('Video save error:', err);
      if (err.error === 'INVALID_SESSION' || err.error === 'NO_SESSION_TOKEN' || err.status === 401) {
        setUploadError(expiredMsg);
      } else if (err.error === 'NOTICE_CHANGED' || err.error === 'NOTICE_UNAVAILABLE') {
        // E3a: the keep-a-copy wording changed while they were reading (server rolled the save back).
        setUploadError("The wording for keeping a copy has changed. Nothing was saved. Please go back and read it again.");
      } else if (err.error === 'VIDEO_TOO_LARGE') {
        setUploadError('This video is too large (max 100MB). Please record a shorter clip.');
      } else if (err.error === 'S3_UPLOAD_FAILED') {
        setUploadError('Upload failed. Please check your connection and try again.');
      } else {
        setUploadError(isEditMode ? 'Save failed. Please try again.' : 'Upload failed. Please try again.');
      }
      setUploading(false);
    }
  }, [isEditMode, hasNewFootage, activeBlob, activeMimeType, activeDuration, memoryLabel, spaceId, navigate, navBase, access, expiredMsg, space, editMemory, groupNoteId]);

  // NEW-128 (PR-7a-style): edit-mode sharing state, mirroring
  // ContributorRecordPage.jsx exactly — seeded from the memory being edited,
  // updated from the PATCH response.
  const [share, setShare] = useState({ visibilityPref: editMemory?.visibilityPref, reviewStatus: editMemory?.reviewStatus, keepCopyExcluded: editMemory?.keepCopyExcluded });   // E3b
  const [sharingBusy, setSharingBusy] = useState(false);
  const [sharingNotice, setSharingNotice] = useState(false);
  const contentDirty = hasNewFootage || (memoryLabel !== (editMemory?.title || ''));
  const applySharing = async (body, failMsg) => {
    if (!editMemory) return;
    setSharingBusy(true); setUploadError(''); setSharingNotice(false);
    try {
      const res = await access.api.patch(`${access.prefix}/${spaceId}/memories/${editMemory.id}`, body);
      setShare((prev) => ({ visibilityPref: res.visibilityPref, reviewStatus: res.reviewStatus, keepCopyExcluded: res.keepCopyExcluded ?? prev.keepCopyExcluded }));   // E3b: response carries keepCopyExcluded only after a change
      if (contentDirty) {
        setSharingNotice(true);
      } else {
        setShowSavedPill(true);
        toastTimerRef.current = setTimeout(() => {
          navigate(`${navBase}/${spaceId}/memories`, { replace: true });
        }, TOAST_DURATION_MS);
      }
    } catch (err) {
      if (err.error === 'INVALID_SESSION' || err.error === 'NO_SESSION_TOKEN' || err.status === 401) setUploadError(expiredMsg);
      else if (err.error === 'NOTICE_CHANGED' || err.error === 'NOTICE_UNAVAILABLE') setUploadError("The wording for keeping a copy has changed. Nothing was saved. Please go back and read it again.");   // E3b
      else setUploadError(failMsg);
    } finally { setSharingBusy(false); }
  };
  const handleShareTier = (vp) => applySharing({ visibilityPref: vp }, "We weren't able to update who can see this. When you're ready, please try again.");
  const handleMakePrivate = () => applySharing({ makePrivate: true }, "We weren't able to stop sharing that memory. When you're ready, please try again.");
  const handleKeepCopy = (excluded, shownHash) => applySharing(excluded ? { keepCopyExcluded: true } : { keepCopyExcluded: false, keepCopyShownHash: shownHash }, "We weren't able to change that. When you're ready, please try again.");   // E3b

  /* ─── Re-record (from review screen) — create flow unchanged from v1.0 ─── */
  function handleReRecord() {
    setUploadError('');
    setMemoryLabel('');
    resetLibrarySelection();
    reRecord();
  }

  /* ─── NEW-128: re-record from edit mode — keeps the caption, matches
     ContributorRecordPage.jsx's wantsReRecord pattern. ─── */
  function handleEditReRecord() {
    setUploadError('');
    setWantsReRecord(true);
    resetLibrarySelection();
    reRecord();
  }

  /* ─── Recording controls ─── */
  function handleRecordTap() {
    if (recordingState === 'recording') pauseRecording();
    else if (recordingState === 'paused') resumeRecording();
  }

  const spaceName = space?.spaceName || 'this space';
  // v2.1 (NEW-163): the quoted leader note (B-8) when responding; null otherwise / in edit mode.
  const respondQuote = (!isEditMode && groupNoteId && space?.isGroupSpace === true && space?.groupSpacesEnabled === true) ? <RespondingToNote groupNotes={space?.groupNotes} noteId={groupNoteId} leaderName={space?.ownerName} /> : null;
  const spacePhotoUrl = space?.photoUrl || null;
  const mirrorClass = facingMode === 'user' ? styles.mirrored : '';
  const isRecording = recordingState === 'recording' || recordingState === 'paused';

  const hintText =
    recordingState === 'recording' ? 'Tap to pause · 5:00 max' :
    recordingState === 'paused'    ? 'Tap to resume · 5:00 max' :
                                     'Tap the circle to start · 5:00 max';

  const stateLabel =
    recordingState === 'recording' ? 'Recording...' :
    recordingState === 'paused'    ? 'Paused' :
                                     'Ready';

  const barSubtitle =
    isEditMode && recordingState === 'idle' && !hasNewFootage ? 'Edit video' :
    (recordingState === 'stopped' && videoBlob) || libraryBlob ? 'Review before saving' :
    recordingState === 'recording'              ? 'Recording...' :
    recordingState === 'paused'                 ? 'Paused' :
                                                  'Ready to record';

  /* ═══════════════════════════════════════════════════════════
     RENDER ORDER: loading → uploading → review → record.
     ═══════════════════════════════════════════════════════════ */

  // ─── 1. Loading ───
  if (loadingData || (isEditMode && loadingExistingVideo)) {
    return (
      <div className={styles.loadingPage}>
        <div className="app-loading-spinner" />
      </div>
    );
  }

  // ─── 2. Load error (pre-space) ───
  if (loadError && !space) {
    return (
      <div className={styles.loadingPage}>
        <p style={{ padding: '24px', textAlign: 'center' }}>{loadError}</p>
      </div>
    );
  }

  // ─── 3. Uploading ───
  if (uploading) {
    return (
      <div className={styles.page}>
        <LovedOneBar
          spaceName={spaceName}
          spacePhotoUrl={spacePhotoUrl}
          subtitle="Saving..."
          onBack={() => {}}
          backLabel="Saving in progress"
        />
        <div className={styles.uploadingContent}>
          <div className="app-loading-spinner" />
          <p className={styles.uploadingText}>Saving your video...</p>
        </div>
        <ContributorBottomNav spaceId={spaceId} activeTab="video" mode={mode} videoEnabled={space?.contributorVideoEnabled} />
      </div>
    );
  }

  // ─── 4. Review (after recording stops, a library file is chosen, or
  //        edit mode with the existing video and no re-record requested) ───
  if ((recordingState === 'stopped' && videoBlob) || libraryBlob || (isEditMode && recordingState === 'idle' && !wantsReRecord)) {
    return (
      <div className={styles.page}>
        <LovedOneBar
          spaceName={spaceName}
          spacePhotoUrl={spacePhotoUrl}
          subtitle="Review before saving"
          onBack={isEditMode ? () => navigate(`${navBase}/${spaceId}/memories`, { replace: true }) : handleReRecord}
          backLabel={isEditMode ? 'Back to memories' : 'Back to recording'}
        />
        {respondQuote}

        <div className={styles.promptWrap}>
          <HardcodedPromptBanner spaceName={spaceName} />
        </div>

        <div className={styles.reviewBody}>
          {/* Video playback card — custom player, same as owner VideoPage.jsx */}
          <div className={styles.playbackCard}>
            <span className={styles.playbackLabel}>YOUR VIDEO</span>

            <div className={styles.videoThumbWrap} onClick={toggleVideoPlayback}>
              {displayVideoUrl && (
                <video
                  ref={reviewVideoRef}
                  className={styles.reviewVideo}
                  src={displayVideoUrl}
                  playsInline
                  preload="auto"
                />
              )}
            </div>

            <div className={styles.videoControlsRow}>
              <button
                className={`${styles.playBtn} ${videoPlaying ? styles.playBtnPlaying : ''}`}
                onClick={toggleVideoPlayback}
                aria-label={videoPlaying ? 'Pause video' : 'Play video'}
              >
                {videoPlaying ? (
                  <svg width="12" height="14" viewBox="0 0 12 14" fill="white">
                    <rect x="0" y="0" width="4" height="14" rx="1" />
                    <rect x="8" y="0" width="4" height="14" rx="1" />
                  </svg>
                ) : (
                  <svg width="12" height="14" viewBox="0 0 12 14" fill="white">
                    <path d="M0 0l12 7-12 7V0z" />
                  </svg>
                )}
              </button>

              <div
                className={styles.progressTrack}
                onClick={handleProgressClick}
                role="slider"
                aria-label="Video progress"
                aria-valuenow={Math.round(videoProgress)}
                aria-valuemin={0}
                aria-valuemax={100}
                tabIndex={0}
              >
                <div className={styles.progressFill} style={{ width: `${videoProgress}%` }} />
              </div>

              <span className={styles.playbackDuration}>
                {videoCurrentTime} / {activeDurationFormatted}
              </span>
            </div>
          </div>

          {/* NEW-128: edit-mode re-record link, right under the player —
              matches ContributorRecordPage.jsx's compact placement exactly. */}
          {isEditMode && (
            <div style={{ textAlign: 'right', marginTop: 8 }}>
              <button
                type="button"
                onClick={handleEditReRecord}
                style={{ background: 'none', border: 'none', padding: 0, color: 'var(--color-sage-dark, #5b7a65)', fontSize: 13, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }}
              >
                Re-record
              </button>
            </div>
          )}

          {/* Settings card — label only (no privacy toggle; sharing tier below) */}
          <div className={styles.settingsCard}>
            <div className={styles.settingsInputRow}>
              <input
                type="text"
                className={styles.labelInput}
                placeholder="Caption (optional) — e.g., Advice, Funny moments..."
                value={memoryLabel}
                onChange={(e) => setMemoryLabel(e.target.value)}
              />
            </div>
          </div>

          {uploadError && (
            <p className={styles.uploadError} role="alert">{uploadError}</p>
          )}

          {isEditMode && space?.groupSpacesEnabled && (
            <ContributorEditSharing
              visibilityPref={share.visibilityPref}
              reviewStatus={share.reviewStatus}
              groupSpacesEnabled={space?.groupSpacesEnabled}
              saving={sharingBusy}
              onShare={handleShareTier}
              onMakePrivate={handleMakePrivate}
              space={space}
              api={access.api}
              keepCopyExcluded={share.keepCopyExcluded}
              onKeepCopyChange={handleKeepCopy}
            />
          )}

          {sharingNotice && (
            <p style={{ fontSize: 13, color: '#6b6459', margin: '4px 2px 8px', textAlign: 'center' }}>
              Your edits to this video aren&rsquo;t saved yet.
            </p>
          )}

          <div className={styles.reviewActions}>
            {isEditMode ? (
              <ContributorEditActions
                onCancel={() => navigate(`${navBase}/${spaceId}/memories`, { replace: true })}
                onSave={() => handleSave()}
                saving={uploading}
              />
            ) : (
              <>
                {space?.groupSpacesEnabled ? (
                  <ContributorSaveActions
                    saving={uploading}
                    onSavePrivate={() => handleSave('contributor_only')}
                    onShare={(vp, kc) => handleSave(vp, kc)}
                space={space}
                api={access.api}
                  />
                ) : (
                  <button className={styles.saveBtn} onClick={() => handleSave()}>
                    {`Add to ${spaceName}'s space`}
                  </button>
                )}
                <button className={styles.reRecordBtn} onClick={handleReRecord}>
                  {libraryBlob && !videoBlob ? 'Choose different video' : 'Re-record'}
                </button>
              </>
            )}
          </div>
        </div>

        <ContributorBottomNav spaceId={spaceId} activeTab="video" mode={mode} videoEnabled={space?.contributorVideoEnabled} />

        {showSavedPill && <SavedPill />}
      </div>
    );
  }

  /* ─── 5. Pre-flight + Recording screen (idle / recording / paused) ─── */
  return (
    <div className={styles.page}>
      <LovedOneBar
        spaceName={spaceName}
        spacePhotoUrl={spacePhotoUrl}
        subtitle={barSubtitle}
        onBack={() => navigate(`${navBase}/${spaceId}/memories`, { replace: true })}
        backLabel="Back to feed"
      />
      {respondQuote}

      <div className={styles.promptWrap}>
        <HardcodedPromptBanner spaceName={spaceName} />
      </div>

      <div className={styles.recordingContent}>
        {/* ─── IDLE: sage circle, no camera yet ─── */}
        {recordingState === 'idle' && !previewStream && (
          <div className={styles.circleArea}>
            <button className={styles.circle} onClick={startRecording} aria-label="Start recording video">
              <div className={styles.ring} />
              <div className={styles.circleIcon}>
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="5" width="14" height="14" rx="2" stroke="var(--color-sage-dark, #5b7a65)" strokeWidth="1.5" />
                  <path d="M16 9.5l6-3.5v12l-6-3.5" stroke="var(--color-sage-dark, #5b7a65)" strokeWidth="1.5" />
                  <circle cx="9" cy="12" r="2" fill="var(--color-sage-dark, #5b7a65)" />
                </svg>
              </div>
            </button>

            <div className={styles.statusArea}>
              <span className={styles.stateLabel}>Ready</span>
              <p className={styles.hint}>Tap the circle to start · 5:00 max</p>
            </div>

            {(recorderError || libraryError) && (
              <p className={styles.recorderError} role="alert">{recorderError || libraryError}</p>
            )}
            {permissionState === 'denied' && (
              <p className={styles.recorderError} role="alert">
                Camera access denied. Please allow access in your browser settings.
              </p>
            )}

            {/* NEW-128: library-picker entry, available in both create and
                re-record contexts, matching owner VideoPage.jsx. */}
            <div className={styles.entryButtons}>
              <button className={styles.libraryBtn} onClick={handleLibraryClick}>
                Choose from Library
              </button>
            </div>
          </div>
        )}

        {/* ─── CAMERA ACTIVE: warm-bordered preview + controls ─── */}
        {(previewStream || isRecording) && (
          <>
            <div className={styles.videoPreviewArea}>
              <video
                ref={liveVideoRef}
                className={`${styles.liveVideo} ${mirrorClass}`}
                autoPlay
                muted
                playsInline
              />

              {!isRecording && (
                <button className={styles.cameraToggleBtn} onClick={toggleCamera} aria-label="Switch camera">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 16v4a2 2 0 0 1-2 2h-4" />
                    <path d="M14 2h4a2 2 0 0 1 2 2v4" />
                    <path d="M4 8V4a2 2 0 0 1 2-2h4" />
                    <path d="M10 22H6a2 2 0 0 1-2-2v-4" />
                    <circle cx="12" cy="12" r="3" />
                  </svg>
                </button>
              )}

              {isRecording && (
                <div className={styles.recordingOverlay}>
                  <div className={`${styles.recordingTimer} ${atWarning ? styles.recordingTimerWarning : ''}`}>
                    <span className={styles.recDot} />
                    <span>{durationFormatted}</span>
                  </div>
                </div>
              )}
            </div>

            <div className={styles.controlsArea}>
              {(recorderError || libraryError) && (
                <p className={styles.recorderError} role="alert">{recorderError || libraryError}</p>
              )}

              {recordingState === 'idle' && (
                <div className={styles.entryButtons}>
                  <button className={styles.startRecordBtn} onClick={startRecording}>
                    <span className={styles.startRecordDot} />
                    Start Recording
                  </button>
                  <button className={styles.libraryBtn} onClick={handleLibraryClick}>
                    Choose from Library
                  </button>
                </div>
              )}

              {isRecording && (
                <>
                  <div className={styles.recordingControls}>
                    <button
                      className={styles.pauseResumeBtn}
                      onClick={handleRecordTap}
                      aria-label={recordingState === 'recording' ? 'Pause recording' : 'Resume recording'}
                    >
                      {recordingState === 'recording' ? (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <rect x="6" y="4" width="4" height="16" rx="1" />
                          <rect x="14" y="4" width="4" height="16" rx="1" />
                        </svg>
                      ) : (
                        <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M6 4l14 8-14 8V4z" />
                        </svg>
                      )}
                      {recordingState === 'recording' ? 'Pause' : 'Resume'}
                    </button>

                    <button className={styles.stopBtn} onClick={stopRecording} aria-label="Stop recording">
                      <span className={styles.stopIcon} />
                      Stop
                    </button>
                  </div>

                  <div className={styles.statusArea}>
                    <span className={styles.stateLabel}>{stateLabel}</span>
                    <p className={styles.hint}>{hintText}</p>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>

      {/* NEW-128: hidden file input for library picker */}
      <input
        ref={fileInputRef}
        type="file"
        accept={ACCEPTED_VIDEO_EXTENSIONS}
        className={styles.fileInput}
        onChange={handleFileSelected}
      />

      <ContributorBottomNav spaceId={spaceId} activeTab="video" mode={mode} videoEnabled={space?.contributorVideoEnabled} />
    </div>
  );
}

/* ═══════════════════════════════════════
   HARDCODED PROMPT BANNER
   Identical copy/pattern to ContributorRecordPage.jsx — contributors don't
   hit the owner prompt endpoints. No skip link.
   ═══════════════════════════════════════ */

function HardcodedPromptBanner({ spaceName }) {
  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #e8f0e8 0%, #d8e8d8 100%)',
        borderTop: '1px solid rgba(124, 152, 133, 0.3)',
        borderBottom: '1px solid rgba(124, 152, 133, 0.3)',
        padding: '20px 24px',
        textAlign: 'center',
      }}
    >
      <span
        style={{
          display: 'block',
          fontSize: '11px',
          fontWeight: 800,
          textTransform: 'uppercase',
          letterSpacing: '0.1em',
          color: '#5b7a65',
          marginBottom: '6px',
          fontFamily: 'var(--font-sans, "DM Sans", -apple-system, sans-serif)',
        }}
      >
        Share a memory
      </span>
      <p
        style={{
          fontSize: '18px',
          fontStyle: 'italic',
          color: '#2d3436',
          margin: 0,
          lineHeight: 1.4,
          fontFamily: 'var(--font-serif, "Lora", Georgia, serif)',
        }}
      >
        Share a memory of {spaceName}.
      </p>
    </div>
  );
}

/* ═══════════════════════════════════════
   SAVED PILL TOAST — identical to ContributorRecordPage.jsx / ContributorWritePage.jsx
   ═══════════════════════════════════════ */

function SavedPill() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        position: 'fixed',
        left: '50%',
        transform: 'translateX(-50%)',
        bottom: 'calc(88px + env(safe-area-inset-bottom, 0px))',
        background: 'var(--color-text-primary, #2d3436)',
        color: '#ffffff',
        padding: '12px 20px',
        borderRadius: '24px',
        fontSize: '14px',
        fontWeight: 500,
        fontFamily: 'var(--font-sans, "DM Sans", -apple-system, sans-serif)',
        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        zIndex: 1000,
        pointerEvents: 'none',
      }}
    >
      <span
        aria-hidden="true"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: '18px',
          height: '18px',
          borderRadius: '50%',
          background: 'var(--color-sage, #7c9885)',
          color: '#ffffff',
          fontSize: '11px',
          lineHeight: 1,
        }}
      >
        ✓
      </span>
      Saved
    </div>
  );
}
